<script>
import { usaStates, imageClassValues } from '@utils/appConstants'
import { required } from 'vuelidate/lib/validators'
import { vinValidator } from '@utils/validators'
import {
  saveState,
  vhrMethods,
  customerMethods,
  referralMethods,
  subscriptionMethods,
  getParameterCaseInsensitive,
  isTokenValid,
} from '@state/helpers'
import _ from 'lodash'
import RecaptchaAttribution from '@components/recaptcha-attribution'

export default {
  components: { RecaptchaAttribution },
  props: {
    searchType: {
      type: Object,
      default() {
        return {
          plate: true,
          vin: true,
        }
      },
    },
    toggle: {
      default: false,
      type: Boolean,
    },
    showScore: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: undefined,
    },
    fromDashboard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      vin: '',
      plate: '',
      plateState: null,
      state: null,
      options: usaStates,
      tryingVinSearch: false,
      tryingPlateSearch: false,
      wasVinValidated: false,
      wasPlateValidated: false,
      vinError: false,
      searchSuccess: _.get(this, '$store.state.vhr.showSummary', false),
      vinSummary: _.get(this, '$store.state.vhr.vehicle', {}),
      vinMax: 17,
      multipleResults: false,
      selectedVehicle: null,
      productError: false,
      products: null,
      plateResults: false,
      fromProductsPage: false,
      currentUserData: _.get(this, '$store.state.auth.currentUser'),
      ownReport: false,
    }
  },
  computed: {
    placeholders() {
      return {
        vin: 'Vehicle Identification Number',
        plate: 'US License Plate',
      }
    },
    vehicleImageClass() {
      if (this.vinSummary.vehicleClass) {
        let vehicleClass
        imageClassValues.forEach((vehicles) => {
          if (vehicles.types.indexOf(this.vinSummary.vehicleClass) > -1) {
            vehicleClass = vehicles.value
          }
        })
        return vehicleClass
      } else {
        return null
      }
    },
  },
  mounted() {
    if (this.$route.path.includes('vehicle-history-reports')) {
      this.fromProductsPage = true
    }
  },
  created() {
    saveState('vhr.vehicle', {})
    saveState('vhr.showSummary', false)
    this.tryingVinSearch = false
    this.searchSuccess = false
    let _vin = getParameterCaseInsensitive(this.$route.query, 'vin')
    if (_vin) {
      this.vin = _vin
      this.vinSearch()
    } else {
      let _plate = getParameterCaseInsensitive(this.$route.query, 'plate')
      let _plateState = getParameterCaseInsensitive(this.$route.query, 'state')
      if (_plate && _plateState) {
        this.plate = _plate
        this.plateState = _plateState
        this.plateSearch()
      }
    }

    // Check if user has subscription - if so show View Report button
    if (this.currentUserData && this.searchSuccess) {
      this.getUserSubscription()
    } else if (this.searchSuccess) {
      this.getUpsellProducts()
    }
  },
  validations: {
    vin: {
      required,
      vinValidator,
    },
    plate: {
      required,
    },
    plateState: {
      required,
    },
  },
  methods: {
    ...vhrMethods,
    ...customerMethods,
    ...referralMethods,
    ...subscriptionMethods,
    getUserSubscription() {
      /// If invalid token timeout
      if (!isTokenValid()) {
        this.$router.push({
          name: 'logoutAndShowSessionTimeout',
        })
        return
      }
      return this.getSubscription()
        .then((response) => {
          let found = false
          if (this.vinSummary && response.allReports) {
            let currentVin = this.vinSummary.vin.toUpperCase()
            for (let el of response.allReports) {
              if (el.vin.toUpperCase() === currentVin && el.active) {
                found = true
                break
              }
            }
          }

          if (response.active) {
            if (response.reportsAvailable > 0 || found) this.ownReport = true
          }

          // If report has already been run and displayed results are already shown
          if (
            !this.ownReport &&
            _.get(this, 'vinSummary.recordCount') !== 0 &&
            this.searchSuccess
          ) {
            this.getUpsellProducts()
          }
        })
        .catch((error) => {
          this.ownReport = false
          this.subscriptionError = error
        })
    },
    findVINPopover() {
      return (
        '<div class="vinPopover">\n' +
        '              <div class="row"><div class="col-7">Each car and truck built after 1980 has been stamped by the manufacturer with a unique vehicle identification number or VIN. The VIN is unique to the vehicle the way your fingerprint is unique to you, and it\'s how we are able to deliver critical vehicle specific data in the AutoCheck® vehicle history report.\n' +
        "                Looking for the VIN? Here's where you'll find it:\n" +
        '                <ul>\n' +
        "                  <li>On the driver's side dashboard near the windshield</li>\n" +
        "                  <li>Inside the driver's side door near the latch</li>\n" +
        "                  <li>On the owner's insurance card</li>\n" +
        '                </ul>\n' +
        '              </div>\n' +
        '              <div class="col-5"><img src="images/car-tooltip.png"></div>\n' +
        '            </div></div>'
      )
    },
    specialtyPlatePopover() {
      return '<div class="platePopover"><img src="images/specialty-plate.png"/><p>TIP: Searching for a specialty license plate? Don\'t forget to include the specialty code -- because we can\'t find the car without it (it\'s usually in tiny letters).</p></div>'
    },
    historicalRecordsPopover() {
      return '<div class="historicalRecordsPopover"><p>A history record is an event reported to AutoCheck by a vetted, reputable source that provides insight into the history of a vehicle and may include accidents, mileage, service history, number of vehicle owners and title brands like salvage and total loss. Get the full vehicle history report to learn more.</p></div>'
    },

    vinSearch() {
      this.$v.$touch()

      // If invalid token timeout
      if (!isTokenValid()) {
        this.$router.push({
          name: 'logoutAndShowSessionTimeout',
        })
        return
      }

      this.ownReport = false
      this.vinError = false
      if (this.$v.vin.$invalid) {
        this.wasVinValidated = true
      } else {
        this.tryingVinSearch = true
        return this.getVinSummary(this.vin)
          .then((response) => {
            this.tryingVinSearch = false
            this.searchSuccess = true
            this.vinSummary = response
            this.getUserSubscription()
            if (!this.products) {
              this.getUpsellProducts()
            }
          })
          .catch((error) => {
            this.tryingVinSearch = false
            this.vinError = error
          })
      }
    },
    vinSearchSelection() {
      // If invalid token timeout
      if (!isTokenValid()) {
        this.$router.push({
          name: 'logoutAndShowSessionTimeout',
        })
        return
      }

      this.ownReport = false
      this.tryingVinSearch = true
      return this.getVinSummary(this.selectedVehicle)
        .then((response) => {
          this.multipleResults = false
          this.tryingVinSearch = false
          this.searchSuccess = true
          this.vinSummary = response
          this.plateResults = true
          this.getUserSubscription()
          if (!this.products) {
            this.getUpsellProducts()
          }
        })
        .catch((error) => {
          this.tryingVinSearch = false
          this.vinError = error
        })
    },
    plateSearch() {
      this.$v.$touch()

      /// If invalid token timeout
      if (!isTokenValid()) {
        this.$router.push({
          name: 'logoutAndShowSessionTimeout',
        })
        return
      }

      this.ownReport = false
      if (this.$v.plateState.$invalid || this.$v.plate.$invalid) {
        this.wasPlateValidated = true
      } else {
        this.tryingPlateSearch = true
        return this.getPlateSummary({
          plate: this.plate,
          selectedState: this.plateState,
        })
          .then((response) => {
            if (response.multiple) {
              this.multipleResults = true
              this.vehicleChoices = response.results
              this.selectedVehicle = response.results[0].vin
            } else {
              this.tryingPlateSearch = false
              this.searchSuccess = true
              this.vinSummary = response.results
              this.plateResults = true
              this.getUserSubscription()
              if (!this.products) {
                this.getUpsellProducts()
              }
            }
          })
          .catch((error) => {
            this.tryingPlateSearch = false
            this.vinError = error
          })
      }
    },
    searchAgain() {
      this.updateSummary(false)
      this.searchSuccess = false
      this.vin = ''
      this.plate = ''
      this.plateState = null
      this.state = null
      this.wasPlateValidated = false
      this.wasVinValidated = false
      this.tryingPlateSearch = false
      this.tryingVinSearch = false
      this.multipleResults = false
      this.plateResults = false
    },
    activateVin() {
      if (this.$route.name === 'dashboard') {
        this.$emit('activate-vin', this.vinSummary.vin)
      } else {
        this.$router.push({
          name: 'full-vehicle-report',
          params: { vin: this.vinSummary.vin },
        })
      }
    },
    toggleSearchType() {
      this.searchType = {
        plate: !this.searchType.plate,
        vin: !this.searchType.vin,
      }
    },
    getUpsellProducts() {
      // If invalid token timeout
      if (!isTokenValid()) {
        this.$router.push({
          name: 'logoutAndShowSessionTimeout',
        })
        return
      }

      this.getProducts()
        .then((response) => {
          this.products = response.filter((product) => product.featured)
        })
        .catch((error) => {
          this.productError = error
        })
    },
    tryToAddToCart(product) {
      // If invalid token timeout
      if (!isTokenValid()) {
        this.$router.push({
          name: 'logoutAndShowSessionTimeout',
        })
        return
      }
      this.addToCart(product)
        .then((response) => {
          this.$router.push({ name: 'checkout' })
        })
        .catch((error) => {
          this.cartError = error
        })
    },
    triggerItemViewGAEvent() {
      if (this.$gtag) {
        for (let product of this.products) {
          const item = {
            item_id: product.code,
            item_name: product.name,
            price: parseFloat(product.price),
            currency: 'USD',
            quantity: 1
          }
        
          this.$gtag.event('view_item', {
            currency: 'USD',
            value: product.price,
            items: [item]
          })
        }   
      }
    }
  },
}
</script>

<template>
  <div
    id="searchVin"
    class="cardContainer cardShadow"
    :class="{ searchCard: !searchSuccess, successSearchCard: searchSuccess }"
  >
    <div class="cardHeaderRow row blueBkg">
      <div v-if="!title && !searchSuccess && !multipleResults" class="col">
        <h2 v-if="searchType.vin == true && searchType.plate == true"
          >AutoCheck Vehicle History Reports</h2
        >
        <h2 v-else-if="searchType.plate == true"
          >Vehicle Search: Enter a Plate</h2
        >
        <h2 v-else>Vehicle Search: Enter a Vin</h2>
      </div>
      <div v-else-if="title && !searchSuccess" class="col">
        <h2>{{ title }}</h2>
      </div>
      <div v-else-if="searchSuccess" class="col">
        <h2>Your Search Results</h2>
      </div>
      <div v-else-if="multipleResults" class="col">
        <h2>&nbsp;</h2>
      </div>
    </div>
    <div v-if="!searchSuccess && !multipleResults" class="cardContent">
      <p v-if="!vinError" class="mt-2 mb-3"
        >Two ways to find the vehicle history you're looking for:</p
      >
      <form
        :class="{ 'was-validated ': wasVinValidated }"
        class="needs-validation"
        @submit.prevent="vinSearch"
      >
        <div v-if="vinError" :class="$style.error">
          <h4>We're sorry: We can't find that car</h4>
          <div class="error-content"
            ><b>Was the car manufactured before 1981?</b> Then it doesn't have
            the VIN we need to find the answers you're looking for.<br /><b
              >Is the car brand new?</b
            >
            It may be so new that there are no records currently on file. Check
            back soon, once it's had some time on the road.
          </div>
        </div>
        <div v-if="searchType.vin == true" id="searchVin">
          <h4>
            Search by VIN
            <span
              v-b-popover.hover.bottom.html="findVINPopover"
              class="clickLink smallLink"
              >Where can I find the VIN? »</span
            >
          </h4>
          <div
            v-if="!$v.vin.vinValidator && $v.vin.required && wasVinValidated"
            style="font-size: 80%;color: #dc3545;"
          >
            <p
              ><strong>Please check your VIN and try again {{ vin }}</strong></p
            >
          </div>
          <div class="row">
            <div class="col-sm-8 col-12">
              <BaseInput
                v-model="$v.vin.$model"
                name="vin"
                :max="vinMax"
                :placeholder="placeholders.vin"
                :has-error="$v.vin.$error && wasVinValidated"
              >
                <div v-if="!$v.vin.required" class="invalid-feedback"
                  >Please enter a vehicle identification number to continue</div
                >
                <div
                  v-if="!$v.vin.vinValidator && $v.vin.required"
                  class="invalid-feedback"
                >
                  A valid vehicle identification number is required. Is it a
                  valid VIN? Valid vehicle identification numbers:
                  <ul class="mb-0">
                    <li>Have exactly 17 characters</li>
                    <li>Come from a vehicle manufactured since 1981</li>
                    <li>Do not contain letters I, O, or Q</li>
                  </ul>
                </div>
              </BaseInput>
            </div>
            <div class="col-sm-4 col-12">
              <BaseButton
                :disabled="tryingVinSearch"
                class="w-100"
                type="submit"
                button-type="buttonPrimary"
              >
                <BaseIcon v-if="tryingVinSearch" name="sync" spin />
                <span v-else>Get Report</span>
              </BaseButton>
            </div>
          </div>
          <div v-if="toggle" class="row">
            <div class="col-12">
              <a class="linkStyle" @click="toggleSearchType()">
                Don't have a VIN? Search by License Plate»
              </a>
            </div>
          </div>
        </div>
      </form>
      <div
        v-if="searchType.plate == true && searchType.vin == true"
        :class="$style.or"
        class="row align-items-center d-flex"
      >
        <div class="col flex-grow-0">
          OR
        </div>
        <div class="col">
          <hr />
        </div>
      </div>

      <div v-if="searchType.plate == true">
        <form
          :class="{ 'was-validated ': wasPlateValidated }"
          class="needs-validation"
          @submit.prevent="plateSearch"
        >
          <h4>
            Search by US License Plate
            <span
              v-b-popover.bottom.hover.html="specialtyPlatePopover"
              class="clickLink smallLink"
              >Is it a specialty plate? »</span
            >
          </h4>
          <div class="row">
            <div class="col-md-5 col-12">
              <BaseInput
                v-model="$v.plate.$model"
                name="plate"
                :placeholder="placeholders.plate"
                :has-error="$v.plate.$error && wasPlateValidated"
              >
                <div v-if="!$v.plate.required" class="invalid-feedback"
                  >Please enter a US License Plate Number to continue</div
                >
              </BaseInput>
            </div>
            <div class="col-md-3 col-8 mb-3">
              <b-form-select
                v-model="$v.plateState.$model"
                name="plateState"
                :options="options"
                :class="{
                  'is-invalid':
                    $v.plateState.$model === null && wasPlateValidated,
                }"
              />
              <div
                v-if="$v.plateState.$model === null && wasPlateValidated"
                class="invalid-feedback"
                >Please select a state</div
              >
            </div>
            <div class="col-md-4 col-12">
              <BaseButton
                :disabled="tryingPlateSearch"
                type="submit"
                button-type="buttonPrimary"
                class="w-100"
              >
                <BaseIcon v-if="tryingPlateSearch" name="sync" spin />
                <span v-else>Get Report</span>
              </BaseButton>
            </div>
          </div>
          <div v-if="toggle" class="row">
            <div class="col-12">
              <a class="linkStyle" @click="toggleSearchType()">
                Don't have a Plate Number? Search by VIN»
              </a>
            </div>
          </div>
          <div v-else-if="!fromProductsPage" class="row mb-1 mt-2">
            <div class="col-12">
              <BaseLink :to="{ name: 'reports-list' }">
                No VIN? Subscribe now, search later»
              </BaseLink>
            </div>
          </div>
        </form>
      </div>
      <div class="row mt-3">
        <div class="col">
          <hr />
          <RecaptchaAttribution />
        </div>
      </div>
    </div>
    <div v-else-if="searchSuccess" class="cardContent">
      <h5 class="learnMoreHeader">Get the full report to learn more:</h5>
      <div class="row">
        <div class="col-4 d-flex align-items-center justify-content-center p-0">
          <div :class="[$style.carsSprite, vehicleImageClass]"> </div>
        </div>
        <div class="col-8" :class="$style.vehicleSummary">
          <h3
            ><span v-if="vinSummary.year && vinSummary.year !== '0'">{{
              vinSummary.year
            }}</span>
            {{ vinSummary.make }} {{ vinSummary.model }}</h3
          >
          <p v-if="!plateResults"
            >VIN: <strong>{{ vinSummary.vin }}</strong></p
          >
          <p
            ><span v-if="vinSummary.body" class="mr-3"
              >Style / Body: <strong>{{ vinSummary.body }}</strong></span
            >
            <span v-if="vinSummary.engine"
              ><span>Engine:</span>
              <strong> {{ vinSummary.engine }}</strong></span
            ></p
          >
          <p v-if="vinSummary.countryOfAssembly"
            >Country of Assembly:
            <strong>{{ vinSummary.countryOfAssembly }}</strong></p
          >
        </div>
      </div>
      <div v-if="showScore">
        <img src="images/logo_autocheck-summary.png" alt="Autocheck Logo" />
        <div
          :class="$style.scoreCard"
          class="d-flex flex-row text-center align-items-center mb-4"
        >
          <div class="col-8">
            <h4 class="mb-0">AutoCheck Score</h4>
            <p class="mb-0">SIMILAR VEHICLE SCORE</p>
            <p class="mb-0"
              ><strong
                >{{ vinSummary.scoreRangeLow }} -
                {{ vinSummary.scoreRangeHigh }}</strong
              ></p
            >
          </div>
          <div class="col-4">
            <h3>{{ vinSummary.score }}</h3>
          </div>
        </div>
      </div>
      <div class="row" :class="$style.records">
        <div class="col-9 d-flex flex-row align-items-center">
          <div>
            <div class="d-flex flex-row align-items-center">
              <BaseIcon name="check-square" :class="$style.checkIcon" />
              <h4
                >We found {{ vinSummary.recordCount }} historical records for
                this vehicle
                <span v-b-popover.hover.bottom.html="historicalRecordsPopover"
                  ><BaseIcon class="questionPopoverIcon" name="question-circle"
                /></span>
              </h4>
            </div>
          </div>
        </div>
        <div v-if="fromDashboard" class="col-3">
          <div :class="$style.badges">
            <div v-if="vinSummary.buybackAssurance === 'THREE_YEARS'">
              <div><img src="images/badge_3year.png"/></div>
            </div>
            <div v-if="vinSummary.buybackAssurance === 'ONE_YEAR'">
              <div><img src="images/badge_1year.png"/></div>
            </div>
            <div v-if="vinSummary.buybackAssurance === 'NOT_ASSURED'">
              <div><img src="images/badge_notassured.png"/></div>
            </div>
          </div>
        </div>
        <div v-else class="col-3"></div>

        <div v-if="vinSummary.status === 'NOT_CORE'" class="col-9 mt-3 mb-2">
          Historical Records are incomplete for this vehicle, a
          {{ vinSummary.year }} {{ vinSummary.make }} {{ vinSummary.model }}.
          Because historical records are incomplete, please be advised that the
          vehicle history report may be missing some key data and does not
          qualify for Buyback Protection.
        </div>
        <div class="col-3"></div>
      </div>
      <div
        v-if="vinSummary.recordCount !== 0 && products && !ownReport"
        class="row"
        :class="$style.upsells"
      >
      {{triggerItemViewGAEvent()}}
        <div
          v-for="(product, index) in products"
          :key="index"
          class="col-6 mt-3"
        >
          <p class="mb-1"
            ><strong>{{ product.name }} ${{ product.price }}</strong></p
          >
          <p class="mb-3">{{ product.featuredDescription }}</p>
          <BaseButton
            button-type="buttonPrimary"
            @click="tryToAddToCart(product)"
          >
            <span
              >{{ product.name.includes('Reports') ? 'Get' : 'Get a' }}
              {{ product.name }}
            </span>
          </BaseButton>
        </div>
      </div>
      <div v-else-if="vinSummary.recordCount !== 0 && ownReport" class="mt-3">
        <BaseButton button-type="buttonPrimary" @click="activateVin">
          <span>
            View Vehicle History Report
          </span>
        </BaseButton>
      </div>
      <hr />
      <div class="row">
        <div class="col-12">
          <span class="clickLink pl-0" @click="searchAgain"
            >Search again »</span
          >
          <BaseLink :to="{ name: 'reports-list' }">
            See all subscription offers »
          </BaseLink>
        </div>
      </div>
    </div>
    <div v-if="multipleResults" class="cardContent">
      <p
        >We found 2 different VINs with that license plate number. Please select
        your vehicle below:</p
      >
      <b-form-group>
        <b-form-radio-group
          v-model="selectedVehicle"
          stacked
          name="radiosStacked"
        >
          <b-form-radio
            v-for="(vehicle, index) in vehicleChoices"
            :key="index"
            class="no-validate mb-2 mt-2"
            :value="vehicle.vin"
            ><span v-if="vehicle.year && vehicle.year !== '0'">{{
              vehicle.year
            }}</span>
            {{ vehicle.make }} {{ vehicle.model }}
          </b-form-radio>
        </b-form-radio-group>
      </b-form-group>
      <BaseButton
        :disabled="tryingVinSearch"
        class="mt-4 mb-4"
        button-type="buttonPrimary"
        @click="vinSearchSelection"
      >
        <BaseIcon v-if="tryingVinSearch" name="sync" spin />
        <span v-else>Continue</span>
      </BaseButton>
      <div class="row">
        <div class="col-12">
          <span class="clickLink pl-0" @click="searchAgain"
            >Or start a new search »</span
          >
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';
.vehicleSummary {
  padding-bottom: 15px;
  font-size: 14px;
  p {
    margin-bottom: 0;
  }
}
.upsells {
  p {
    font-size: 14px;
  }
}
.checkIcon {
  margin-right: 14px;
  font-size: 40px;
  color: #00ad2e;
}

.or {
  margin-top: 10px;
  margin-bottom: 15px;
  font-size: 18px;
  font-style: italic;
  font-weight: bold;
  color: #979797;
}
.records {
  padding: 15px;
  margin-right: -30px;
  margin-left: -30px;
  background-color: #f1f1f1;
  h4 {
    margin-bottom: 0;
  }
}

.carsSprite {
  width: 100px;
  height: 65px;
  background: url('@assets/images/cars-images.png') no-repeat 0 0;
}

.scoreCard {
  padding: 15px;
  color: #fff;
  background-color: #004895;
  border-radius: 5px;
  h4 {
    color: #fff;
  }
  h3 {
    font-size: 54px;
    color: #fff;
  }
  .scoreNumber {
    font-size: 40px;
  }
}
.error {
  margin-bottom: 10px;
  color: #d9262a;
  h4 {
    color: #d9262a;
  }
}
.sprite {
  margin-right: 0;
  background: url('@assets/images/vhr_sprite.png');
}
</style>
